import { fixedDigitsValue } from "helpers/dataHelpers";
import { ComboGroupSubItem, CustomerMenuProduct } from "types/customers";

export const getComboGroupsItemsNumberTitle = (min?: number, max?: number) => {
  if (min === undefined || max === undefined) {
    return "";
  }
  if (min === max) {
    return `${min} item(s)`;
  }
  if (min === 0 && max === 0) {
    return "No items";
  }
  if (min === 0) {
    return `Up to ${max} items`;
  }
  if (max === 0) {
    return `At least ${min} items`;
  }
  return `From ${min} to ${max} items`;
};

export const getProductItemPrice = (product: CustomerMenuProduct | null) => {
  const fmcPrice = product?.fmcCentsPrice ?? 0;
  const fiatPrice = product?.fiatCentsPrice ?? 0;

  if (!fmcPrice && !fiatPrice && product?.comboGroups?.length) {
    const requridedCombos = product?.comboGroups?.filter(
      group => group.min === 1 && group.max === 1,
    );

    const minimumProductPrice = requridedCombos?.reduce<{
      fiat: number;
      cents: number;
    }>(
      (acc, group) => {
        const minItem = group.subItems.reduce<ComboGroupSubItem | null>(
          (min, item) => {
            if (!min || item.fmcCentsPrice < min.fmcCentsPrice) return item;
            return min;
          },
          null,
        );

        acc = minItem
          ? { cents: minItem.fmcCentsPrice, fiat: minItem.fiatCentsPrice }
          : { fiat: 0, cents: 0 };

        return acc;
      },
      { fiat: 0, cents: 0 },
    );

    return `${fixedDigitsValue(
      minimumProductPrice.cents,
      1,
    )} | KD ${fixedDigitsValue(minimumProductPrice.fiat, 3)}`;
  }

  return `${fixedDigitsValue(product.fmcCentsPrice, 1)} | KD ${fixedDigitsValue(
    product.fiatCentsPrice,
    3,
  )}`;
};

export const getProductInputPrice = (
  product: CustomerMenuProduct | null,
  selectedCombos: Record<string, ComboGroupSubItem[]> | null,
): string => {
  if (!product) {
    return `${fixedDigitsValue(0, 1)} | KD ${fixedDigitsValue(0, 3)}`;
  }

  const baseCoinsPrice = product.fmcCentsPrice ?? 0;
  const baseFiatPrice = product.fiatCentsPrice ?? 0;

  if (
    baseCoinsPrice > 0 ||
    baseFiatPrice > 0 ||
    !product.comboGroups?.length ||
    !selectedCombos
  ) {
    return `${fixedDigitsValue(baseCoinsPrice, 1)} | KD ${fixedDigitsValue(
      baseFiatPrice,
      3,
    )}`;
  }

  const combosPrice = product.comboGroups
    .filter(group => group.min === 1 && group.max === 1)
    .reduce(
      (total, group) => {
        const groupItems = selectedCombos[group.id];
        if (!groupItems?.length) return total;

        const groupTotals = groupItems.reduce(
          (sum, item) => ({
            coins: sum.coins + (item?.fmcCentsPrice ?? 0),
            fiat: sum.fiat + (item?.fiatCentsPrice ?? 0),
          }),
          { coins: 0, fiat: 0 },
        );

        return {
          coins: total.coins + groupTotals.coins,
          fiat: total.fiat + groupTotals.fiat,
        };
      },
      { coins: 0, fiat: 0 },
    );

  return `${fixedDigitsValue(combosPrice.coins, 1)} | KD ${fixedDigitsValue(
    combosPrice.fiat,
    3,
  )}`;
};
